import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Handshake, Search, Loader2, MapPin, Send } from "lucide-react";
import FunnelTriggerButton from "@/components/FunnelTriggerButton";
import { useAuth } from "@/context/AuthContext";

const FUNNEL_META = [
  ["welcome", "Benvenuto (all'ingresso del partner)"],
  ["vesta", "Saldo punti Vesta (ad ogni nuovo ordine)"],
  ["inattivo", "Sollecito inattività"],
  ["scadenza", "Contratto in scadenza"],
  ["scaduto", "Contratto scaduto"],
];

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const fullAddress = (p) => (p.address || [p.cap, p.city, p.province ? `(${p.province})` : ""].filter(Boolean).join(" ")).trim() || "—";
const classeOf = (p) => p.partner_class || (p.profilazione || {}).categoria_auto || "—";
const CAT_STYLE = { A: "bg-emerald-600 text-white", B: "bg-primary text-primary-foreground", C: "bg-amber-500 text-white", D: "bg-muted text-muted-foreground" };

function ScadenzaCell({ p }) {
  const scad = p.partnership_contract?.data_scadenza;
  if (!scad) return <Badge variant="outline" className="border-red-500/60 text-red-500 text-[10px]" data-testid="scad-mancante">Da caricare</Badge>;
  const days = Math.ceil((new Date(scad + "T00:00:00") - new Date()) / 86400000);
  return (
    <div className="flex items-center gap-2" data-testid="scad-cell">
      <span className="font-mono text-sm">{itDate(scad)}</span>
      {days < 0 && <Badge className="bg-red-600 text-white text-[10px]" data-testid="scad-scaduto">Scaduto</Badge>}
      {days >= 0 && days <= 30 && <Badge className="bg-amber-500 text-white text-[10px]">In scadenza ({days}g)</Badge>}
    </div>
  );
}

export default function PortafoglioPartner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAgency = user?.role === "agency";
  const showFunnelCol = isAdmin || isAgency; // admin: modificabile · agenzia: sola lettura · altri ruoli: nascosto
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");
  const [agency, setAgency] = useState("all");
  const [kpi, setKpi] = useState(null);
  const [only60, setOnly60] = useState(false);
  const [onlyNoContract, setOnlyNoContract] = useState(false);
  const [onlyInactive, setOnlyInactive] = useState(false);
  const currentYear = new Date().getFullYear();
  const [testEmail, setTestEmail] = useState("");
  const [provaK, setProvaK] = useState(null);

  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const openCfg = async () => {
    setCfgOpen(true);
    try {
      const r = await api.get("/partner-funnel-config");
      setCfg(r.data);
    } catch {
      toast.error("Impossibile caricare la configurazione del funnel");
    }
  };
  useEffect(() => {
    api.get("/partner-funnel-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  const setBlk = (k, field, val) => setCfg((c) => ({ ...c, [k]: { ...c[k], [field]: val } }));
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const body = { attivo_globale: !!cfg.attivo_globale, giorni_inattivo: Number(cfg.giorni_inattivo) || 90, giorni_preavviso: Number(cfg.giorni_preavviso) || 60 };
      FUNNEL_META.forEach(([k]) => { body[k] = { attivo: !!cfg[k].attivo, subject: cfg[k].subject, testo: cfg[k].testo }; });
      const r = await api.put("/partner-funnel-config", body);
      setCfg(r.data);
      toast.success("Configurazione funnel salvata");
      setCfgOpen(false);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Non hai i permessi per modificare i testi" : "Salvataggio non riuscito");
    } finally {
      setSavingCfg(false);
    }
  };

  const inviaProva = async (k) => {
    setProvaK(k);
    try {
      const r = await api.post("/partner-funnel-config/invia-prova", { ftype: k, email: testEmail.trim() || undefined, subject: cfg[k].subject, testo: cfg[k].testo });
      toast.success(`Email di prova inviata a ${r.data?.to || "te"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio prova fallito");
    } finally {
      setProvaK(null);
    }
  };

  const toggleFunnel = async (p) => {
    const next = p.funnel_attivo === false;
    setRows((rs) => rs.map((r) => (r.id === p.id ? { ...r, funnel_attivo: next } : r)));
    try {
      await api.put(`/partners/${p.id}/funnel`, { attivo: next });
      toast.success(next ? "Funnel attivato" : "Funnel disattivato");
    } catch {
      setRows((rs) => rs.map((r) => (r.id === p.id ? { ...r, funnel_attivo: !next } : r)));
      toast.error("Impossibile aggiornare il funnel");
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get("/partners", { params: { presidio: "all" } })
      .then((r) => setRows((r.data || []).filter((p) => p.is_partner)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    api.get("/dashboard").then((r) => setKpi(r.data?.kpis || null)).catch(() => {});
  }, []);

  const regions = useMemo(() => [...new Set(rows.map((r) => r.region).filter(Boolean))].sort(), [rows]);
  const agencies = useMemo(() => [...new Set(rows.map((r) => r.agency_name).filter(Boolean))].sort(), [rows]);

  const isExp60 = (p) => { const s = p.partnership_contract?.data_scadenza; if (!s) return false; return Math.ceil((new Date(s + "T00:00:00") - new Date()) / 86400000) <= 60; };
  const base = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (region !== "all" && p.region !== region) return false;
      if (agency !== "all" && p.agency_name !== agency) return false;
      if (term && !([p.name, p.city, p.province, p.agency_name, p.address].filter(Boolean).join(" ").toLowerCase().includes(term))) return false;
      return true;
    });
  }, [rows, q, region, agency]);
  const exp60Count = useMemo(() => base.filter(isExp60).length, [base]);
  const senzaContratto = base.filter((p) => !p.partnership_contract).length;
  const inactiveCount = useMemo(() => base.filter((p) => (p.ordini_anno || 0) <= 0).length, [base]);
  let filtered = base;
  if (only60) filtered = filtered.filter(isExp60);
  if (onlyNoContract) filtered = filtered.filter((p) => !p.partnership_contract);
  if (onlyInactive) filtered = filtered.filter((p) => (p.ordini_anno || 0) <= 0);
  const scaduti = base.filter((p) => { const s = p.partnership_contract?.data_scadenza; return s && new Date(s + "T00:00:00") < new Date(); }).length;

  return (
    <div className="space-y-6" data-testid="portafoglio-partner-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            <Handshake className="h-7 w-7 text-green-600" /> Portafoglio Partner
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="pp-summary">
            {loading ? "Caricamento…" : <>{filtered.length} partner{scaduti > 0 ? <> · <span className="font-semibold text-red-500">{scaduti} con contratto scaduto</span></> : ""}{senzaContratto > 0 ? <> · <button type="button" onClick={() => setOnlyNoContract((v) => !v)} data-testid="pp-senza-contratto" className={`font-semibold underline-offset-2 hover:underline ${onlyNoContract ? "text-amber-600" : "text-muted-foreground"}`}>{senzaContratto} senza contratto</button></> : ""}</>}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {cfg && <Badge className={cfg.attivo_globale ? "bg-green-600 hover:bg-green-600" : "bg-muted text-muted-foreground hover:bg-muted"} data-testid="pp-funnel-global-state">Funnel {cfg.attivo_globale ? "ON" : "OFF"}</Badge>}
            <FunnelTriggerButton label="Funnel partner" onClick={openCfg} data-testid="pp-configura-funnel" />
          </div>
        )}
      </div>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="funnel-config-dialog">
          <DialogHeader>
            <DialogTitle>Configura funnel Partner — email di testo</DialogTitle>
          </DialogHeader>
          {!cfg ? (
            <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div className={`flex items-center justify-between rounded-lg border p-3 ${cfg.attivo_globale ? "border-green-600/40 bg-green-600/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="cfg-global-block">
                <div>
                  <div className="text-sm font-semibold">Funnel partner attivo (interruttore generale)</div>
                  <div className="text-xs text-muted-foreground">{cfg.attivo_globale ? "Attivo: le email automatiche del funnel possono partire." : "Spento: nessuna email automatica del funnel viene inviata (né benvenuto, né solleciti)."}</div>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <span className={cfg.attivo_globale ? "font-semibold text-green-600" : "font-semibold text-amber-600"}>{cfg.attivo_globale ? "ON" : "OFF"}</span>
                  <Switch checked={!!cfg.attivo_globale} onCheckedChange={(v) => setCfg((c) => ({ ...c, attivo_globale: v }))} data-testid="cfg-attivo-globale" />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">Le email vengono inviate al partner (in copia all'agente di competenza e al referente direzionale) in testo semplice. Attiva/disattiva il funnel per singolo partner dalla tabella.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Giorni di inattività per il sollecito</Label><Input type="number" value={cfg.giorni_inattivo} onChange={(e) => setCfg((c) => ({ ...c, giorni_inattivo: e.target.value }))} data-testid="cfg-giorni-inattivo" /></div>
                <div className="space-y-1"><Label className="text-xs">Preavviso scadenza contratto (giorni)</Label><Input type="number" value={cfg.giorni_preavviso} onChange={(e) => setCfg((c) => ({ ...c, giorni_preavviso: e.target.value }))} data-testid="cfg-giorni-preavviso" /></div>
              </div>
              <div className="space-y-1 rounded-lg border border-dashed border-border p-3">
                <Label className="text-xs">Email per invio di prova</Label>
                <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Lascia vuoto per usare la tua email" data-testid="cfg-test-email" />
                <p className="text-[11px] text-muted-foreground">La prova usa i testi attuali (anche non salvati) con valori di esempio; nessuna copia (CC) ai destinatari reali.</p>
              </div>
              {FUNNEL_META.map(([k, label]) => (
                <div key={k} className="space-y-2 rounded-lg border border-border p-3" data-testid={`cfg-block-${k}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{label}</div>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="sm" onClick={() => inviaProva(k)} disabled={provaK === k} data-testid={`cfg-invia-prova-${k}`}>
                        {provaK === k ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />} Invia prova
                      </Button>
                      <label className="flex items-center gap-2 text-xs">
                        <span className={cfg[k].attivo ? "text-green-600" : "text-muted-foreground"}>{cfg[k].attivo ? "Attiva" : "Off"}</span>
                        <Switch checked={!!cfg[k].attivo} onCheckedChange={(v) => setBlk(k, "attivo", v)} data-testid={`cfg-attivo-${k}`} />
                      </label>
                    </div>
                  </div>
                  <Input value={cfg[k].subject} onChange={(e) => setBlk(k, "subject", e.target.value)} placeholder="Oggetto email" data-testid={`cfg-subject-${k}`} />
                  <Textarea value={cfg[k].testo} onChange={(e) => setBlk(k, "testo", e.target.value)} rows={k === "welcome" ? 14 : 6} className="text-xs" data-testid={`cfg-testo-${k}`} />
                  {cfg._placeholders?.[k] && <p className="text-[11px] text-muted-foreground">Segnaposto disponibili: <span className="font-mono">{cfg._placeholders[k].join("  ")}</span></p>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCfgOpen(false)}>Annulla</Button>
            <Button onClick={saveCfg} disabled={savingCfg || !cfg} data-testid="cfg-save">{savingCfg && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4" data-testid="kpi-perc-partner-attivi">
          <div className="text-xs text-muted-foreground">% Partner attivi</div>
          <div className="font-heading text-2xl font-bold">{kpi ? `${kpi.perc_partner_attivi ?? 0}%` : "—"}</div>
          <div className="text-[11px] text-muted-foreground">{kpi ? `${kpi.partner_attivi ?? 0} su ${kpi.partner_firmati ?? 0} con ordini (ultimi 12 mesi)` : ""}</div>
        </Card>
        <Card className="p-4" data-testid="kpi-perc-fatturato-partner">
          <div className="text-xs text-muted-foreground">% Fatturato da partner</div>
          <div className="font-heading text-2xl font-bold">{kpi ? `${kpi.tasso_copertura ?? 0}%` : "—"}</div>
          <div className="text-[11px] text-muted-foreground">quota fatturato generato dai partner</div>
        </Card>
        <button type="button" onClick={() => setOnly60((v) => !v)} data-testid="kpi-scadenza-60"
          className={`rounded-lg border p-4 text-left transition ${only60 ? "border-amber-500 bg-amber-500/10" : "border-border hover:bg-accent/40"}`}>
          <div className="text-xs text-muted-foreground">Contratti in scadenza (60 gg)</div>
          <div className="font-heading text-2xl font-bold text-amber-600">{exp60Count}</div>
          <div className="text-[11px] text-muted-foreground">{only60 ? "Filtro attivo · clicca per rimuovere" : "Clicca per filtrare i rinnovi"}</div>
        </button>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca per nome, città, indirizzo, agenzia…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pp-search" />
          </div>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-[180px]" data-testid="pp-region"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le regioni</SelectItem>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={agency} onValueChange={setAgency}>
            <SelectTrigger className="w-[220px]" data-testid="pp-agency"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le agenzie</SelectItem>{agencies.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" variant={onlyInactive ? "default" : "outline"} onClick={() => setOnlyInactive((v) => !v)} data-testid="pp-only-inactive" className={onlyInactive ? "bg-red-600 hover:bg-red-600" : ""}>
            Inattivi {currentYear} ({inactiveCount})
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead><MapPin className="mr-1 inline h-3.5 w-3.5" />Indirizzo completo</TableHead>
                  <TableHead>Regione</TableHead>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Classe</TableHead>
                  <TableHead className="text-right">Acquisti {currentYear}</TableHead>
                  <TableHead>Scadenza contratto</TableHead>
                  {showFunnelCol && <TableHead className="text-center">Funnel</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-accent/50" onClick={() => navigate(`/partners/${p.id}`)} data-testid={`pp-row-${p.id}`}>
                    <TableCell className="font-semibold">{p.name}{p.city ? <span className="block text-[11px] font-normal text-muted-foreground">{p.city}</span> : null}</TableCell>
                    <TableCell className="text-sm">
                      <Link to={`/partners/${p.id}#mappa-pin`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline" data-testid={`pp-address-${p.id}`} title="Apri la scheda per modificare la posizione sulla mappa">{fullAddress(p)}</Link>
                    </TableCell>
                    <TableCell className="text-sm">{p.region || "—"}</TableCell>
                    <TableCell className="text-sm">{p.agency_name || <span className="text-muted-foreground">Direzionale</span>}</TableCell>
                    <TableCell><Badge className={`text-[11px] ${CAT_STYLE[classeOf(p)] || "bg-muted text-muted-foreground"}`}>{classeOf(p)}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-sm" data-testid={`pp-acquisti-${p.id}`}>{(p.ordini_anno || 0) > 0 ? eur(p.ordini_anno) : <span className="font-semibold text-red-500">0 €</span>}</TableCell>
                    <TableCell><ScadenzaCell p={p} /></TableCell>
                    {showFunnelCol && (
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center" title={p.funnel_attivo === false ? "Funnel disattivato" : "Funnel attivo"}>
                          {isAdmin ? (
                            <Switch checked={p.funnel_attivo !== false} onCheckedChange={() => toggleFunnel(p)} data-testid={`pp-funnel-${p.id}`} />
                          ) : (
                            <Badge className={p.funnel_attivo !== false ? "bg-green-600 hover:bg-green-600 text-white text-[10px]" : "bg-muted text-muted-foreground hover:bg-muted text-[10px]"} data-testid={`pp-funnel-ro-${p.id}`}>{p.funnel_attivo !== false ? "Attivo" : "Off"}</Badge>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={showFunnelCol ? 8 : 7} className="py-10 text-center text-muted-foreground" data-testid="pp-empty">Nessun partner trovato.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
